import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@crisellevaldez/speculo-ui';
import { getRolloutStages, saveRolloutStages, RolloutStage } from '../../api/rollout';

interface UseRolloutStagesOptions {
  orgId?: string;
  enabled?: boolean;
}

/**
 * Hook for managing FUB stages via Rollout
 * Fetches stages from Rollout and saves enabled stages to Firestore
 */
export const useRolloutStages = ({ orgId, enabled = true }: UseRolloutStagesOptions) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Query to fetch stages from Rollout
  const stagesQuery = useQuery({
    queryKey: ['rollout-stages', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('Organization ID is required');
      const response = await getRolloutStages(orgId);
      return response.stages;
    },
    enabled: enabled && !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation to save enabled stages
  const saveMutation = useMutation({
    mutationFn: async (stages: RolloutStage[]) => {
      if (!orgId) throw new Error('Organization ID is required');

      // Spec asks for {id, title}; deployed backend currently reads {id, name}.
      // Send both so we're forward- and backward-compatible.
      const enabledStages = stages
        .filter((s) => s.enabled)
        .map((s) => {
          const label = s.title ?? s.name ?? '';
          return { id: s.id, title: label, name: label };
        });

      const response = await saveRolloutStages(orgId, enabledStages);

      return response;
    },
    // Optimistically update the cached stages list so callers see the new
    // enabled/disabled state without waiting for the GET refetch round-trip.
    // Rolled back in onError if the save fails.
    onMutate: async (nextStages) => {
      await queryClient.cancelQueries({ queryKey: ['rollout-stages', orgId] });
      const previous = queryClient.getQueryData<RolloutStage[]>(['rollout-stages', orgId]);
      queryClient.setQueryData<RolloutStage[]>(['rollout-stages', orgId], nextStages);
      return { previous };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rollout-stages', orgId] });
      queryClient.invalidateQueries({ queryKey: ['Organizations'] });
      showToast({
        message: 'Stages saved successfully!',
        variant: 'success',
      });
    },
    onError: (error, _vars, context) => {
      // Restore the prior cache so the UI doesn't lie when the save failed.
      if (context?.previous) {
        queryClient.setQueryData(['rollout-stages', orgId], context.previous);
      }
      console.error('Error saving stages:', error);
      showToast({
        message: 'Failed to save stages. Please try again.',
        variant: 'error',
      });
    },
  });

  return {
    /** Available stages from FUB */
    stages: stagesQuery.data || [],
    /** Loading state for stages */
    isLoading: stagesQuery.isLoading,
    /** Error state */
    error: stagesQuery.error,
    /** Refetch stages */
    refetch: stagesQuery.refetch,
    /** Save enabled stages */
    saveStages: saveMutation.mutateAsync,
    /** Whether save is in progress */
    isSaving: saveMutation.isPending,
  };
};
